import type { ParseKeys } from 'i18next';

export const subscriptionPlans = ['free', 'pro'] as const;
export type SubscriptionPlan = (typeof subscriptionPlans)[number];

// Feature message keys are composed at runtime from the mapper below
// (`${planSlug}.${section}.${feature}`). The plan/feature combinations are static, so the
// composed strings are always real keys — assert that here, once, against the typed catalogue
// so call sites can pass `feature.name`/`feature.detail` straight to `t()` without a cast.
type PlanFeatureKey = Extract<ParseKeys, `team.subscriptions.plans.${string}`>;

export type Feature = {
  name: PlanFeatureKey;
  detail: PlanFeatureKey;
};

export const subscriptionMessageMapper: Record<
  SubscriptionPlan,
  { shapeGeneral: string[]; issueTracker: string[]; shiftManager: string[]; gallery?: string[]; dataBook: string[] }
> = {
  free: {
    shapeGeneral: ['collaborators', 'realtimeProjectTimeline'],
    issueTracker: ['fullIssueHistory', 'privateChannelPerIssue', 'offlineIssueCapture'],
    shiftManager: [
      'createSiteDiary',
      'resourceAllocation',
      'addPhotoEvidenceToAnyLineItem',
      'professionalPDFOutputs',
      'saveEffortWithDataPrefill',
    ],
    dataBook: ['dataHealthHeatmap'],
    gallery: [
      'createSiteDiary',
      'automaticCentralisedStorage',
      'addCaptionsAndLocationsToAllFiles',
      'allPhotosAutoUploaded',
    ],
  },
  pro: {
    shapeGeneral: ['collaborators', 'realtimeProjectTimeline'],
    issueTracker: ['everythingInFreePlus', 'excelDataExport', 'pdfIssueExport', 'generateSingleIssueReport'],
    shiftManager: ['everythingInFreePlus', 'saveTimeWithMultiUserMode', 'excelDataExport', 'topDownDataQualityView'],
    dataBook: ['dataHealthHeatmap', 'customDashboards', 'dataInsights'],
  },
};

type getPlanFeatureProps = {
  planSlug: SubscriptionPlan;
};

type GetPlanFeature = (props: getPlanFeatureProps) => Feature[];

export const getPlanShapeGeneralFeatures: GetPlanFeature = ({ planSlug }) =>
  subscriptionMessageMapper[planSlug].shapeGeneral.map((feature) => ({
    name: `team.subscriptions.plans.${planSlug}.shapeGeneral.${feature}.name` as PlanFeatureKey,
    detail: `team.subscriptions.plans.${planSlug}.shapeGeneral.${feature}.detail` as PlanFeatureKey,
  }));

export const getPlanIssueTrackerFeatures: GetPlanFeature = ({ planSlug }) =>
  subscriptionMessageMapper[planSlug].issueTracker.map((feature) => ({
    name: `team.subscriptions.plans.${planSlug}.issueTracker.${feature}.name` as PlanFeatureKey,
    detail: `team.subscriptions.plans.${planSlug}.issueTracker.${feature}.detail` as PlanFeatureKey,
  }));

export const getPlanShiftManagerFeatures: GetPlanFeature = ({ planSlug }) =>
  subscriptionMessageMapper[planSlug].shiftManager.map((feature) => ({
    name: `team.subscriptions.plans.${planSlug}.shiftManager.${feature}.name` as PlanFeatureKey,
    detail: `team.subscriptions.plans.${planSlug}.shiftManager.${feature}.detail` as PlanFeatureKey,
  }));

export const getPlanDataBookFeatures: GetPlanFeature = ({ planSlug }) =>
  subscriptionMessageMapper[planSlug].dataBook.map((feature) => ({
    name: `team.subscriptions.plans.${planSlug}.dataBook.${feature}.name` as PlanFeatureKey,
    detail: `team.subscriptions.plans.${planSlug}.dataBook.${feature}.detail` as PlanFeatureKey,
  }));

export const getPlanGalleryFeatures: GetPlanFeature = ({ planSlug }) =>
  subscriptionMessageMapper[planSlug].gallery?.map((feature) => ({
    name: `team.subscriptions.plans.${planSlug}.gallery.${feature}.name` as PlanFeatureKey,
    detail: `team.subscriptions.plans.${planSlug}.gallery.${feature}.detail` as PlanFeatureKey,
  })) || [];
